import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteIcon,
  EditOutlined as EditIcon,
  OpenInNew as OpenInNewIcon,
  FileDownload as ExportIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from "@mui/icons-material";

import DashboardLayout from "../../layouts/DashboardLayout";
import MDBox from "../../components/MDBox";
import { wholesalersApi } from "../../services/api/omni/wholesalers";
import { WH_DIR_CACHE_KEY, readCache, writeCache } from "./cache";
import {
  CATEGORIES,
  COUNTRIES,
  COUNTRY_FLAGS,
  GOODS_TYPES,
  LIST_SOURCES,
  PAYMENT_TERMS,
  PRICE_LEVELS,
  STATUSES,
  YES_NO_OPTIONS,
  emptyForm,
  formatMoq,
  fromApiRow,
  statusLabel,
  toApiPayload,
} from "./constants";
import CredentialsTab from "./CredentialsTab";
import OrdersTab from "./OrdersTab";

const DRAWER_TABS = [
  "Overview",
  "Terms",
  "Price list & data",
  "Brands",
  "History",
];

const useWhStyles = () => {
  const theme = useTheme();
  const dark = theme.palette.mode === "dark";
  return {
    dark,
    card: dark ? "#16203a" : "#ffffff",
    card2: dark ? "#1b2745" : "#f4f6fb",
    border: dark ? "#253254" : "#e3e8f2",
    muted: dark ? "#8b9ab8" : "#6b7a94",
    text: dark ? "#e9eefb" : "#1a2336",
    rowHover: dark ? "#1e2a4a" : "#f0f4fb",
    blue: "#2f6fed",
    green: "#1e9e6a",
    red: "#c0392b",
    amber: "#f0a92b",
    theadBorder: dark ? "#1e2842" : "#e8edf5",
    rowBorder: dark ? "#1a2440" : "#eef1f7",
    brandBg: dark ? "#202d4d" : "#eef2fa",
    brandBorder: dark ? "#2b3a60" : "#d5deed",
    brandText: dark ? "#b9c6e2" : "#4a5a78",
    tagOffenBg: dark ? "#1c3b7a" : "#dce9ff",
    tagOffenText: dark ? "#bcd2ff" : "#1c4ea3",
    tagRuhendBg: dark ? "#3a2a30" : "#f3e4e7",
    tagRuhendText: dark ? "#d0a2ac" : "#8a3a4a",
    accessBg: dark ? "#15224a" : "#eaf0ff",
    accessBorder: dark ? "#1d3f8f" : "#b7c9f5",
    boxBg: dark ? "#131d35" : "#f7f9fc",
    footBg: dark ? "#131c31" : "#f5f7fb",
  };
};

const Field = ({ label, children, wide = false, hint }) => {
  const s = useWhStyles();
  return (
    <Box sx={{ gridColumn: wide ? "1 / -1" : "auto" }}>
      <Typography
        sx={{
          fontSize: 11.5,
          textTransform: "uppercase",
          letterSpacing: 0.6,
          color: s.muted,
          fontWeight: 600,
          mb: 0.6,
        }}
      >
        {label}
      </Typography>
      {children}
      {hint && (
        <Typography sx={{ mt: 0.5, fontSize: 11.5, color: s.muted }}>
          {hint}
        </Typography>
      )}
    </Box>
  );
};

const inputSx = (s) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: s.card2,
    borderRadius: "8px",
    fontSize: 13.5,
    "& fieldset": { borderColor: s.border },
    "&:hover fieldset": { borderColor: s.blue },
  },
});

const SourceFields = ({ listSource, sourceDetails, onChange, s }) => {
  const fields = LIST_SOURCES[listSource]?.fields || [];
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
        gap: 1.5,
        mt: 1.5,
      }}
    >
      {fields.map(([key, label]) => (
        <Field key={key} label={label}>
          <TextField
            fullWidth
            size="small"
            value={sourceDetails?.[key] || ""}
            onChange={(e) =>
              onChange({ ...sourceDetails, [key]: e.target.value })
            }
            sx={inputSx(s)}
          />
        </Field>
      ))}
    </Box>
  );
};

const SourceChips = ({ value, onChange, s }) => (
  <Box
    sx={{
      border: `1px solid ${s.accessBorder}`,
      background: s.accessBg,
      borderRadius: "10px",
      p: 1.75,
    }}
  >
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      mb={1.25}
    >
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: s.text }}>
        Price-list source
      </Typography>
      <Typography sx={{ fontSize: 11, color: s.muted }}>
        determines which fields appear below
      </Typography>
    </Stack>
    <Stack direction="row" flexWrap="wrap" gap={1}>
      {Object.entries(LIST_SOURCES).map(([name, meta]) => {
        const on = value === name;
        return (
          <Box
            key={name}
            onClick={() => onChange(name)}
            sx={{
              px: 1.5,
              py: 0.85,
              borderRadius: 999,
              cursor: "pointer",
              fontSize: 12.5,
              display: "inline-flex",
              alignItems: "center",
              gap: 0.75,
              background: on ? s.blue : s.dark ? "#1b2b56" : "#fff",
              border: `1px solid ${on ? s.blue : s.dark ? "#2c407a" : s.border}`,
              color: on ? "#fff" : s.text,
              fontWeight: on ? 600 : 500,
              "&:hover": { borderColor: s.blue },
            }}
          >
            <span>{meta.icon}</span>
            {name}
          </Box>
        );
      })}
    </Stack>
  </Box>
);

const StatusTag = ({ status, s }) => {
  if (status === "active") return null;
  const offen = status === "requested";
  return (
    <Box
      component="span"
      sx={{
        fontSize: 9.5,
        fontWeight: 700,
        letterSpacing: 0.5,
        textTransform: "uppercase",
        px: 0.75,
        py: 0.25,
        borderRadius: "4px",
        background: offen ? s.tagOffenBg : s.tagRuhendBg,
        color: offen ? s.tagOffenText : s.tagRuhendText,
      }}
    >
      {statusLabel(status)}
    </Box>
  );
};

const PricePill = ({ level, s }) => {
  if (!level) return <Typography sx={{ color: s.muted }}>—</Typography>;
  const bg =
    level === "A"
      ? s.green
      : level === "C"
        ? s.red
        : s.dark
          ? "#33405f"
          : "#dfe5f0";
  const color =
    level === "A"
      ? "#03291b"
      : level === "C"
        ? "#fff"
        : s.dark
          ? "#c5d0e8"
          : "#334";
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: 1.25,
        py: 0.35,
        borderRadius: 999,
        fontSize: 11.5,
        fontWeight: 600,
        background: bg,
        color,
      }}
    >
      {level}
    </Box>
  );
};

const StatusPill = ({ status, s }) => {
  const bg =
    status === "active"
      ? s.green
      : status === "requested"
        ? s.blue
        : s.dark
          ? "#33405f"
          : "#dfe5f0";
  const color =
    status === "active"
      ? "#03291b"
      : status === "requested"
        ? "#fff"
        : s.dark
          ? "#c5d0e8"
          : "#334";
  return (
    <Box
      sx={{
        display: "inline-flex",
        px: 1.25,
        py: 0.35,
        borderRadius: 999,
        fontSize: 11.5,
        fontWeight: 600,
        background: bg,
        color,
      }}
    >
      {statusLabel(status)}
    </Box>
  );
};

const WholesalerFormFields = ({
  form,
  setForm,
  showExtras = false,
  showSteps = false,
}) => {
  const s = useWhStyles();
  const set = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const Step = ({ children }) =>
    showSteps ? (
      <Typography
        sx={{
          gridColumn: "1 / -1",
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: 0.9,
          color: s.blue,
          fontWeight: 700,
          mt: 1,
          mb: 0.5,
          display: "flex",
          alignItems: "center",
          gap: 1,
          "&::after": {
            content: '""',
            flex: 1,
            height: "1px",
            background: s.border,
          },
        }}
      >
        {children}
      </Typography>
    ) : null;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
        gap: 1.6,
      }}
    >
      <Step>Master data</Step>
      <Field label="Company *">
        <TextField
          fullWidth
          size="small"
          value={form.company_name}
          onChange={set("company_name")}
          sx={inputSx(s)}
        />
      </Field>
      <Field label="Country *">
        <FormControl fullWidth size="small" sx={inputSx(s)}>
          <Select value={form.country} onChange={set("country")}>
            {COUNTRIES.map((c) => (
              <MenuItem key={c} value={c}>
                {COUNTRY_FLAGS[c] || ""} {c}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Field>
      <Field label="Category">
        <FormControl fullWidth size="small" sx={inputSx(s)}>
          <Select value={form.category} onChange={set("category")}>
            {CATEGORIES.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Field>
      <Field label="Status">
        <FormControl fullWidth size="small" sx={inputSx(s)}>
          <Select value={form.status} onChange={set("status")}>
            {STATUSES.map((st) => (
              <MenuItem key={st.value} value={st.value}>
                {st.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Field>
      <Field label="Website" wide>
        <TextField
          fullWidth
          size="small"
          value={form.website}
          onChange={set("website")}
          sx={inputSx(s)}
        />
      </Field>
      <Field label="Contact">
        <TextField
          fullWidth
          size="small"
          value={form.contact_name}
          onChange={set("contact_name")}
          sx={inputSx(s)}
        />
      </Field>
      <Field label="Email">
        <TextField
          fullWidth
          size="small"
          value={form.email}
          onChange={set("email")}
          sx={inputSx(s)}
        />
      </Field>

      <Step>How do we get the price list?</Step>
      <Box sx={{ gridColumn: "1 / -1" }}>
        <SourceChips
          value={form.list_source}
          onChange={(list_source) =>
            setForm((prev) => ({
              ...prev,
              list_source,
              source_details: {},
            }))
          }
          s={s}
        />
        <SourceFields
          listSource={form.list_source}
          sourceDetails={form.source_details}
          onChange={(source_details) =>
            setForm((prev) => ({ ...prev, source_details }))
          }
          s={s}
        />
      </Box>

      <Step>Terms</Step>
      <Field label="Min. order (€)">
        <TextField
          fullWidth
          size="small"
          type="number"
          value={form.min_order_value}
          onChange={set("min_order_value")}
          sx={inputSx(s)}
        />
      </Field>
      <Field label="Payment terms">
        <FormControl fullWidth size="small" sx={inputSx(s)}>
          <Select value={form.payment_terms} onChange={set("payment_terms")}>
            {PAYMENT_TERMS.map((p) => (
              <MenuItem key={p} value={p}>
                {p}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Field>
      <Field label="Price level">
        <FormControl fullWidth size="small" sx={inputSx(s)}>
          <Select value={form.price_level} onChange={set("price_level")}>
            {PRICE_LEVELS.map((p) => (
              <MenuItem key={p.value} value={p.value}>
                {p.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Field>
      <Field label="Goods">
        <FormControl fullWidth size="small" sx={inputSx(s)}>
          <Select value={form.goods_type} onChange={set("goods_type")}>
            {GOODS_TYPES.map((g) => (
              <MenuItem key={g} value={g}>
                {g}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Field>
      <Field label="Top brands" wide hint="Comma-separated, best brand first">
        <TextField
          fullWidth
          size="small"
          value={form.brands}
          onChange={set("brands")}
          sx={inputSx(s)}
        />
      </Field>
      <Field label="Note" wide>
        <TextField
          fullWidth
          size="small"
          multiline
          minRows={3}
          value={form.note}
          onChange={set("note")}
          sx={inputSx(s)}
        />
      </Field>

      {showExtras && (
        <>
          <Field label="Customer no.">
            <TextField
              fullWidth
              size="small"
              value={form.customer_number}
              onChange={set("customer_number")}
              sx={inputSx(s)}
            />
          </Field>
          <Field label="Owner">
            <TextField
              fullWidth
              size="small"
              value={form.owner}
              onChange={set("owner")}
              sx={inputSx(s)}
            />
          </Field>
          <Field label="EAN in list">
            <FormControl fullWidth size="small" sx={inputSx(s)}>
              <Select value={form.ean_in_list} onChange={set("ean_in_list")}>
                {YES_NO_OPTIONS.map((o) => (
                  <MenuItem key={o} value={o}>
                    {o}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Field>
          <Field label="Dropshipping">
            <FormControl fullWidth size="small" sx={inputSx(s)}>
              <Select value={form.dropshipping} onChange={set("dropshipping")}>
                {YES_NO_OPTIONS.map((o) => (
                  <MenuItem key={o} value={o}>
                    {o}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Field>
          <Field label="Amazon-ready invoice">
            <FormControl fullWidth size="small" sx={inputSx(s)}>
              <Select
                value={form.amazon_invoice_ok}
                onChange={set("amazon_invoice_ok")}
              >
                {YES_NO_OPTIONS.map((o) => (
                  <MenuItem key={o} value={o}>
                    {o}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Field>
          <Field label="Activated since">
            <TextField
              fullWidth
              size="small"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={form.activated_since || ""}
              onChange={set("activated_since")}
              sx={inputSx(s)}
            />
          </Field>
          <Field label="Last contact">
            <TextField
              fullWidth
              size="small"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={form.last_contact_at || ""}
              onChange={set("last_contact_at")}
              sx={inputSx(s)}
            />
          </Field>
        </>
      )}
    </Box>
  );
};

const KvBox = ({ title, rows, s }) => (
  <Box
    sx={{
      mt: 2,
      background: s.boxBg,
      border: `1px solid ${s.border}`,
      borderRadius: "10px",
      p: 1.75,
    }}
  >
    <Typography
      sx={{
        mb: 1.25,
        fontSize: 12,
        textTransform: "uppercase",
        letterSpacing: 0.7,
        color: s.muted,
        fontWeight: 600,
      }}
    >
      {title}
    </Typography>
    {rows.map(([k, v]) => (
      <Stack
        key={k}
        direction="row"
        justifyContent="space-between"
        gap={2}
        sx={{
          py: 0.75,
          borderBottom: `1px dashed ${s.border}`,
          fontSize: 13,
          "&:last-child": { borderBottom: "none" },
        }}
      >
        <Typography sx={{ color: s.muted, fontSize: 13 }}>{k}</Typography>
        <Typography sx={{ color: s.text, fontSize: 13 }}>{v || "—"}</Typography>
      </Stack>
    ))}
  </Box>
);

const WholesalersPage = () => {
  const s = useWhStyles();
  const [pageTab, setPageTab] = useState(0);
  const [allItems, setAllItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [q, setQ] = useState("");
  const [statusView, setStatusView] = useState("active");
  const [category, setCategory] = useState("");
  const [listSource, setListSource] = useState("");
  const [country, setCountry] = useState("");
  const [sortBy, setSortBy] = useState("company_name");
  const [sortDir, setSortDir] = useState("asc");

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState(0);
  const [selected, setSelected] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm());
  const [historyNote, setHistoryNote] = useState("");

  const applyDirectoryPayload = useCallback((list, summaryData) => {
    setAllItems(list);
    setSummary(summaryData);
    writeCache(WH_DIR_CACHE_KEY, { items: list, summary: summaryData });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [listRes, summaryRes] = await Promise.all([
        wholesalersApi.list({
          sort_by: "company_name",
          sort_dir: "asc",
        }),
        wholesalersApi.summary(),
      ]);
      applyDirectoryPayload(listRes.data.items || [], summaryRes.data);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err.message ||
          "Failed to load wholesalers",
      );
    } finally {
      setLoading(false);
    }
  }, [applyDirectoryPayload]);

  useEffect(() => {
    const cached = readCache(WH_DIR_CACHE_KEY);
    if (cached?.items && cached?.summary) {
      setAllItems(cached.items);
      setSummary(cached.summary);
      setLoading(false);
      return;
    }
    load();
  }, [load]);

  const items = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let rows = [...allItems];
    if (statusView !== "all") {
      rows = rows.filter((r) => r.status === statusView);
    }
    if (category) rows = rows.filter((r) => r.category === category);
    if (listSource) rows = rows.filter((r) => r.list_source === listSource);
    if (country) rows = rows.filter((r) => r.country === country);
    if (needle) {
      rows = rows.filter((r) => {
        const hay = [
          r.company_name,
          r.country,
          r.contact_name,
          r.email,
          r.note,
          r.category,
          r.list_source,
          ...(Array.isArray(r.brands) ? r.brands : []),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(needle);
      });
    }
    const dir = sortDir === "desc" ? -1 : 1;
    rows.sort((a, b) => {
      const av = a[sortBy];
      const bv = b[sortBy];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") {
        return (av - bv) * dir;
      }
      return (
        String(av).localeCompare(String(bv), undefined, {
          sensitivity: "base",
        }) * dir
      );
    });
    return rows;
  }, [allItems, q, statusView, category, listSource, country, sortBy, sortDir]);

  const openCreate = () => {
    setCreateForm(emptyForm());
    setCreateOpen(true);
  };

  const openDrawer = (row) => {
    setSelected(row);
    setEditForm(fromApiRow(row));
    setDrawerTab(0);
    setHistoryNote("");
    setDrawerOpen(true);
  };

  const handleCreate = async () => {
    if (!createForm.company_name.trim()) {
      setError("Company name is required");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await wholesalersApi.create(toApiPayload(createForm));
      setCreateOpen(false);
      setSuccess(`${createForm.company_name} created`);
      setStatusView(createForm.status);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await wholesalersApi.update(
        selected.id,
        toApiPayload(editForm),
      );
      setSelected(updated.data);
      setEditForm(fromApiRow(updated.data));
      setSuccess("Saved");
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (
      !window.confirm(
        `Delete ${row.company_name}?\n\nAny purchase orders linked to this wholesaler will also be deleted.`,
      )
    ) {
      return;
    }
    try {
      await wholesalersApi.remove(row.id);
      if (selected?.id === row.id) setDrawerOpen(false);
      setSuccess("Deleted");
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "Delete failed");
    }
  };

  const handleAddHistory = async () => {
    if (!selected || !historyNote.trim()) return;
    try {
      await wholesalersApi.addHistory(selected.id, {
        note: historyNote.trim(),
        event_type: "note",
      });
      const refreshed = await wholesalersApi.get(selected.id);
      setSelected(refreshed.data);
      setEditForm(fromApiRow(refreshed.data));
      setHistoryNote("");
      setSuccess("History entry added");
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "History failed");
    }
  };

  const exportCsv = () => {
    const headers = [
      "company_name",
      "country",
      "category",
      "status",
      "list_source",
      "min_order_value",
      "price_level",
      "brands",
      "email",
      "contact_name",
      "last_contact_at",
    ];
    const lines = [headers.join(";")];
    items.forEach((row) => {
      lines.push(
        headers
          .map((h) => {
            let val = row[h];
            if (Array.isArray(val)) val = val.join(", ");
            return `"${String(val ?? "").replace(/"/g, '""')}"`;
          })
          .join(";"),
      );
    });
    const blob = new Blob([lines.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "wholesalers.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortBy(key);
      setSortDir("asc");
    }
  };

  const sortMark = (key) =>
    sortBy === key ? (sortDir === "asc" ? " ↑" : " ↓") : " ⇅";

  const statusCounts = useMemo(
    () => ({
      active: summary?.active ?? 0,
      requested: summary?.requested ?? 0,
      dormant: summary?.dormant ?? 0,
      all: summary?.total ?? 0,
    }),
    [summary],
  );

  const gridCols = {
    xs: "minmax(0,1fr) 120px",
    md: "minmax(200px,2.4fr) 120px 150px 100px 70px 100px 88px",
  };

  const HeadCell = ({ sortKey, label, sx = {} }) => (
    <Box
      onClick={sortKey ? () => toggleSort(sortKey) : undefined}
      sx={{
        color: sortBy === sortKey ? s.text : s.muted,
        fontSize: 11.5,
        textTransform: "uppercase",
        letterSpacing: 0.7,
        fontWeight: 600,
        cursor: sortKey ? "pointer" : "default",
        userSelect: "none",
        ...sx,
      }}
    >
      {label}
      {sortKey ? sortMark(sortKey) : ""}
    </Box>
  );

  const btnSx = {
    textTransform: "none",
    borderRadius: "8px",
    fontWeight: 500,
    fontSize: 13,
    px: 1.75,
    py: 0.9,
    borderColor: s.border,
    color: s.text,
    background: s.card2,
    "&:hover": { background: s.rowHover, borderColor: s.border },
  };

  return (
    <DashboardLayout>
      <MDBox>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && pageTab === 0 && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            onClose={() => setSuccess(null)}
          >
            {success}
          </Alert>
        )}

        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs
            value={pageTab}
            onChange={(_, v) => {
              setPageTab(v);
              setError(null);
              setSuccess(null);
            }}
          >
            <Tab label="Directory" />
            <Tab label="Orders" />
            <Tab label="Credentials" />
          </Tabs>
        </Box>

        {pageTab === 1 ? (
          <OrdersTab />
        ) : pageTab === 2 ? (
          <CredentialsTab s={s} />
        ) : (
          <>
            {/* Page head + strip */}
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", md: "flex-start" }}
              spacing={2}
              mb={1}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: 30,
                    fontWeight: 600,
                    letterSpacing: "-0.4px",
                    color: s.text,
                    lineHeight: 1.15,
                  }}
                >
                  Wholesalers
                </Typography>
                {summary && (
                  <Stack
                    direction="row"
                    flexWrap="wrap"
                    alignItems="baseline"
                    sx={{ mt: 1 }}
                  >
                    {[
                      [summary.active, "active"],
                      [summary.countries, "countries"],
                      [summary.brands, "brands"],
                      [summary.with_feed_or_api, "with feed or API"],
                    ].map(([n, label], i, arr) => (
                      <Stack
                        key={label}
                        direction="row"
                        alignItems="baseline"
                        spacing={0.9}
                        sx={{
                          pr: 2.2,
                          mr: 2.2,
                          borderRight:
                            i < arr.length - 1
                              ? `1px solid ${s.border}`
                              : "none",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 20,
                            fontWeight: 600,
                            fontVariantNumeric: "tabular-nums",
                            color: s.text,
                          }}
                        >
                          {n}
                        </Typography>
                        <Typography sx={{ color: s.muted, fontSize: 12.5 }}>
                          {label}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Box>
              <Stack
                direction="row"
                spacing={1.1}
                sx={{ pt: { md: 1 } }}
                flexWrap="wrap"
                useFlexGap
              >
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={load}
                  disabled={loading}
                  sx={btnSx}
                >
                  {loading ? "Loading…" : "Reload"}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ExportIcon />}
                  onClick={exportCsv}
                  disabled={!items.length}
                  sx={btnSx}
                >
                  Export
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={openCreate}
                  sx={{
                    ...btnSx,
                    background: s.blue,
                    borderColor: s.blue,
                    color: "#fff",
                    fontWeight: 600,
                    "&:hover": { background: "#3b7bf5" },
                  }}
                >
                  Add wholesaler
                </Button>
              </Stack>
            </Stack>

            {/* Toolbar */}
            <Stack
              direction="row"
              flexWrap="wrap"
              gap={1.25}
              alignItems="center"
              sx={{ mt: 2.5, mb: 1.75 }}
            >
              <TextField
                size="small"
                placeholder="Search company, brand, country, contact…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                sx={{
                  flex: 1,
                  minWidth: 230,
                  ...inputSx(s),
                  "& .MuiOutlinedInput-root": {
                    ...inputSx(s)["& .MuiOutlinedInput-root"],
                    background: s.card,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: s.muted, fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl size="small" sx={{ minWidth: 150, ...inputSx(s) }}>
                <Select
                  displayEmpty
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  sx={{ background: s.card }}
                >
                  <MenuItem value="">All categories</MenuItem>
                  {CATEGORIES.map((c) => (
                    <MenuItem key={c} value={c}>
                      {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 170, ...inputSx(s) }}>
                <Select
                  displayEmpty
                  value={listSource}
                  onChange={(e) => setListSource(e.target.value)}
                  sx={{ background: s.card }}
                >
                  <MenuItem value="">Any list source</MenuItem>
                  {Object.keys(LIST_SOURCES).map((src) => (
                    <MenuItem key={src} value={src}>
                      {src}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140, ...inputSx(s) }}>
                <Select
                  displayEmpty
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  sx={{ background: s.card }}
                >
                  <MenuItem value="">All countries</MenuItem>
                  {COUNTRIES.map((c) => (
                    <MenuItem key={c} value={c}>
                      {COUNTRY_FLAGS[c]} {c}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box
                sx={{
                  display: "flex",
                  background: s.card,
                  border: `1px solid ${s.border}`,
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                {[
                  ["active", `Active (${statusCounts.active})`],
                  ["requested", `Requested (${statusCounts.requested})`],
                  ["dormant", `Dormant (${statusCounts.dormant})`],
                  ["all", `All (${statusCounts.all})`],
                ].map(([key, label]) => (
                  <Box
                    key={key}
                    component="button"
                    onClick={() => setStatusView(key)}
                    sx={{
                      border: "none",
                      px: 1.5,
                      py: 1,
                      cursor: "pointer",
                      fontSize: 12.5,
                      background: statusView === key ? s.blue : "transparent",
                      color: statusView === key ? "#fff" : s.muted,
                      fontWeight: statusView === key ? 600 : 500,
                    }}
                  >
                    {label}
                  </Box>
                ))}
              </Box>
            </Stack>
            <Typography sx={{ color: s.muted, fontSize: 12.5, mb: 1.5 }}>
              {items.length} {items.length === 1 ? "entry" : "entries"} shown
            </Typography>

            {/* Table */}
            <Box
              sx={{
                background: s.card,
                borderRadius: "10px",
                overflow: "hidden",
                border: `1px solid ${s.border}`,
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: gridCols,
                  gap: 1.5,
                  alignItems: "center",
                  px: 2.5,
                  py: 1.5,
                  borderBottom: `1px solid ${s.theadBorder}`,
                }}
              >
                <HeadCell sortKey="company_name" label="Wholesaler" />
                <HeadCell
                  sortKey="country"
                  label="Country"
                  sx={{ display: { xs: "none", md: "block" } }}
                />
                <HeadCell
                  sortKey="list_source"
                  label="List source"
                  sx={{ display: { xs: "none", md: "block" } }}
                />
                <HeadCell
                  sortKey="min_order_value"
                  label="Min. order"
                  sx={{ display: { xs: "none", md: "block" } }}
                />
                <HeadCell
                  sortKey="price_level"
                  label="Price"
                  sx={{ display: { xs: "none", md: "block" } }}
                />
                <HeadCell
                  sortKey="last_contact_at"
                  label="Last"
                  sx={{ display: { xs: "none", md: "block" } }}
                />
                <Box
                  sx={{
                    textAlign: "right",
                    color: s.muted,
                    fontSize: 11.5,
                    textTransform: "uppercase",
                    letterSpacing: 0.7,
                    fontWeight: 600,
                  }}
                >
                  Actions
                </Box>
              </Box>

              {loading ? (
                <Box sx={{ p: 5, textAlign: "center", color: s.muted }}>
                  Loading…
                </Box>
              ) : items.length === 0 ? (
                <Box sx={{ p: 5.5, textAlign: "center", color: s.muted }}>
                  <Typography sx={{ color: s.text, fontWeight: 600, mb: 0.75 }}>
                    No wholesaler found
                  </Typography>
                  Reset filters or add a new wholesaler.
                </Box>
              ) : (
                items.map((row) => {
                  const src = LIST_SOURCES[row.list_source] || {
                    icon: "•",
                    color: s.muted,
                  };
                  const brands = Array.isArray(row.brands) ? row.brands : [];
                  const notePreview = row.note
                    ? String(row.note).slice(0, 58) +
                      (String(row.note).length > 58 ? "…" : "")
                    : "";
                  return (
                    <Box
                      key={row.id}
                      onClick={() => openDrawer(row)}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: gridCols,
                        gap: 1.5,
                        alignItems: "center",
                        px: 2.5,
                        py: 1.5,
                        borderBottom: `1px solid ${s.rowBorder}`,
                        cursor: "pointer",
                        "&:last-child": { borderBottom: "none" },
                        "&:hover": { background: s.rowHover },
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          gap={1}
                          flexWrap="wrap"
                        >
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: 13.8,
                              color: s.text,
                            }}
                          >
                            {row.company_name}
                          </Typography>
                          <StatusTag status={row.status} s={s} />
                        </Stack>
                        <Box
                          sx={{
                            mt: 0.4,
                            color: s.muted,
                            fontSize: 11.8,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {brands.slice(0, 3).map((b) => (
                            <Box
                              key={b}
                              component="span"
                              sx={{
                                display: "inline-block",
                                background: s.brandBg,
                                border: `1px solid ${s.brandBorder}`,
                                color: s.brandText,
                                px: 0.75,
                                borderRadius: "5px",
                                fontSize: 10.5,
                                mr: 0.5,
                              }}
                            >
                              {b}
                            </Box>
                          ))}
                          {notePreview}
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          display: { xs: "none", md: "flex" },
                          alignItems: "center",
                          gap: 0.75,
                          color: s.muted,
                          fontSize: 12.5,
                        }}
                      >
                        <span>{COUNTRY_FLAGS[row.country] || "🏳️"}</span>
                        {row.country}
                      </Box>

                      <Box
                        sx={{
                          display: { xs: "none", md: "flex" },
                          alignItems: "center",
                          gap: 0.75,
                          fontSize: 12.3,
                          color: s.text,
                        }}
                      >
                        <Box
                          sx={{
                            width: 7,
                            height: 7,
                            borderRadius: "50%",
                            background: src.color,
                            flex: "0 0 7px",
                          }}
                        />
                        {src.icon} {row.list_source}
                      </Box>

                      <Typography
                        sx={{
                          display: { xs: "none", md: "block" },
                          fontVariantNumeric: "tabular-nums",
                          fontSize: 13,
                          color: formatMoq(row.min_order_value)
                            ? s.text
                            : s.muted,
                        }}
                      >
                        {formatMoq(row.min_order_value) || "no MOQ"}
                      </Typography>

                      <Box sx={{ display: { xs: "none", md: "block" } }}>
                        <PricePill level={row.price_level} s={s} />
                      </Box>

                      <Typography
                        sx={{
                          display: { xs: "none", md: "block" },
                          color: s.muted,
                          fontSize: 12.5,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {row.last_contact_at || "—"}
                      </Typography>

                      <Stack
                        direction="row"
                        justifyContent="flex-end"
                        spacing={0.5}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <IconButton
                          size="small"
                          disabled={!row.website}
                          onClick={() =>
                            window.open(row.website, "_blank", "noopener")
                          }
                          sx={{ color: s.blue }}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => openDrawer(row)}
                          sx={{ color: s.blue }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(row)}
                          sx={{ color: "#e04b3a" }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Box>
                  );
                })
              )}
            </Box>

            {/* Create modal */}
            <Dialog
              open={createOpen}
              onClose={() => setCreateOpen(false)}
              maxWidth="md"
              fullWidth
              PaperProps={{
                sx: {
                  borderRadius: "14px",
                  background: s.card,
                  border: `1px solid ${s.border}`,
                },
              }}
            >
              <DialogTitle sx={{ pb: 0.5 }}>
                <Typography
                  sx={{ fontSize: 19, fontWeight: 600, color: s.text }}
                >
                  Add wholesaler
                </Typography>
                <Typography sx={{ mt: 0.5, color: s.muted, fontSize: 12.5 }}>
                  Required: company, country, and how you get the price list.
                </Typography>
              </DialogTitle>
              <DialogContent dividers sx={{ borderColor: s.border }}>
                <WholesalerFormFields
                  form={createForm}
                  setForm={setCreateForm}
                  showSteps
                />
              </DialogContent>
              <DialogActions
                sx={{ background: s.footBg, px: 2.5, py: 1.75, gap: 1 }}
              >
                <Button onClick={() => setCreateOpen(false)} sx={btnSx}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  disabled={saving}
                  onClick={handleCreate}
                  sx={{
                    ...btnSx,
                    background: s.blue,
                    color: "#fff",
                    fontWeight: 600,
                    "&:hover": { background: "#3b7bf5" },
                  }}
                >
                  Create wholesaler
                </Button>
              </DialogActions>
            </Dialog>

            {/* Detail drawer */}
            <Drawer
              anchor="right"
              open={drawerOpen}
              onClose={() => setDrawerOpen(false)}
              PaperProps={{
                sx: {
                  width: { xs: "100%", sm: 560 },
                  background: s.card,
                  borderLeft: `1px solid ${s.border}`,
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                }}
              >
                <Box sx={{ px: 2.75, pt: 2.5, position: "relative" }}>
                  <IconButton
                    onClick={() => setDrawerOpen(false)}
                    sx={{
                      position: "absolute",
                      top: 12,
                      right: 12,
                      color: s.muted,
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                  <Typography
                    sx={{
                      fontSize: 20,
                      fontWeight: 600,
                      color: s.text,
                      pr: 4,
                      mb: 0.75,
                    }}
                  >
                    {editForm.company_name || "—"}
                  </Typography>
                  <Stack
                    direction="row"
                    spacing={1.5}
                    flexWrap="wrap"
                    alignItems="center"
                    sx={{ color: s.muted, fontSize: 12.5 }}
                  >
                    <StatusPill status={editForm.status} s={s} />
                    <span>
                      {COUNTRY_FLAGS[editForm.country] || ""} {editForm.country}
                    </span>
                    <span>{editForm.category}</span>
                    {editForm.customer_number && (
                      <span>Cust. {editForm.customer_number}</span>
                    )}
                  </Stack>

                  <Stack
                    direction="row"
                    sx={{
                      mt: 2,
                      borderBottom: `1px solid ${s.theadBorder}`,
                      overflowX: "auto",
                    }}
                  >
                    {DRAWER_TABS.map((label, i) => (
                      <Box
                        key={label}
                        component="button"
                        onClick={() => setDrawerTab(i)}
                        sx={{
                          background: "none",
                          border: "none",
                          borderBottom: `2px solid ${
                            drawerTab === i ? s.blue : "transparent"
                          }`,
                          px: 1.5,
                          py: 1.1,
                          color: drawerTab === i ? s.text : s.muted,
                          cursor: "pointer",
                          fontSize: 13,
                          whiteSpace: "nowrap",
                          fontWeight: drawerTab === i ? 600 : 500,
                        }}
                      >
                        {label}
                      </Box>
                    ))}
                  </Stack>
                </Box>

                <Box sx={{ flex: 1, overflow: "auto", px: 2.75, py: 2.25 }}>
                  {drawerTab === 0 && (
                    <>
                      <WholesalerFormFields
                        form={editForm}
                        setForm={setEditForm}
                        showExtras
                      />
                      <KvBox
                        title="At a glance"
                        s={s}
                        rows={[
                          ["Activated since", editForm.activated_since],
                          ["Owner", editForm.owner],
                          ["Last contact", editForm.last_contact_at],
                          ["Customer no.", editForm.customer_number],
                        ]}
                      />
                    </>
                  )}

                  {drawerTab === 1 && (
                    <>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 1.6,
                        }}
                      >
                        <Field label="Min. order (€)">
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            value={editForm.min_order_value}
                            onChange={(e) =>
                              setEditForm((p) => ({
                                ...p,
                                min_order_value: e.target.value,
                              }))
                            }
                            sx={inputSx(s)}
                          />
                        </Field>
                        <Field label="Payment terms">
                          <FormControl fullWidth size="small" sx={inputSx(s)}>
                            <Select
                              value={editForm.payment_terms}
                              onChange={(e) =>
                                setEditForm((p) => ({
                                  ...p,
                                  payment_terms: e.target.value,
                                }))
                              }
                            >
                              {PAYMENT_TERMS.map((p) => (
                                <MenuItem key={p} value={p}>
                                  {p}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Field>
                        <Field label="Price level">
                          <FormControl fullWidth size="small" sx={inputSx(s)}>
                            <Select
                              value={editForm.price_level}
                              onChange={(e) =>
                                setEditForm((p) => ({
                                  ...p,
                                  price_level: e.target.value,
                                }))
                              }
                            >
                              {PRICE_LEVELS.map((p) => (
                                <MenuItem key={p.value} value={p.value}>
                                  {p.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Field>
                        <Field label="Goods">
                          <FormControl fullWidth size="small" sx={inputSx(s)}>
                            <Select
                              value={editForm.goods_type}
                              onChange={(e) =>
                                setEditForm((p) => ({
                                  ...p,
                                  goods_type: e.target.value,
                                }))
                              }
                            >
                              {GOODS_TYPES.map((g) => (
                                <MenuItem key={g} value={g}>
                                  {g}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Field>
                        <Field label="Dropshipping">
                          <FormControl fullWidth size="small" sx={inputSx(s)}>
                            <Select
                              value={editForm.dropshipping}
                              onChange={(e) =>
                                setEditForm((p) => ({
                                  ...p,
                                  dropshipping: e.target.value,
                                }))
                              }
                            >
                              {YES_NO_OPTIONS.map((o) => (
                                <MenuItem key={o} value={o}>
                                  {o}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Field>
                        <Field label="Amazon-ready invoice">
                          <FormControl fullWidth size="small" sx={inputSx(s)}>
                            <Select
                              value={editForm.amazon_invoice_ok}
                              onChange={(e) =>
                                setEditForm((p) => ({
                                  ...p,
                                  amazon_invoice_ok: e.target.value,
                                }))
                              }
                            >
                              {YES_NO_OPTIONS.map((o) => (
                                <MenuItem key={o} value={o}>
                                  {o}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Field>
                      </Box>
                      <KvBox
                        title="Why this matters"
                        s={s}
                        rows={[
                          [
                            "Ungating-ready invoice",
                            editForm.amazon_invoice_ok,
                          ],
                          ["Goods classification", editForm.goods_type],
                        ]}
                      />
                    </>
                  )}

                  {drawerTab === 2 && (
                    <>
                      <SourceChips
                        value={editForm.list_source}
                        onChange={(list_source) =>
                          setEditForm((p) => ({ ...p, list_source }))
                        }
                        s={s}
                      />
                      <SourceFields
                        listSource={editForm.list_source}
                        sourceDetails={editForm.source_details}
                        onChange={(source_details) =>
                          setEditForm((p) => ({ ...p, source_details }))
                        }
                        s={s}
                      />
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 1.6,
                          mt: 2,
                        }}
                      >
                        <Field label="EAN in list">
                          <FormControl fullWidth size="small" sx={inputSx(s)}>
                            <Select
                              value={editForm.ean_in_list}
                              onChange={(e) =>
                                setEditForm((p) => ({
                                  ...p,
                                  ean_in_list: e.target.value,
                                }))
                              }
                            >
                              {YES_NO_OPTIONS.map((o) => (
                                <MenuItem key={o} value={o}>
                                  {o}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Field>
                        <Field label="Suitable for n8n">
                          <TextField
                            fullWidth
                            size="small"
                            value={
                              ["API / Interface", "FTP feed"].includes(
                                editForm.list_source,
                              )
                                ? "Yes – automatable"
                                : "No – manual fetch"
                            }
                            InputProps={{ readOnly: true }}
                            sx={inputSx(s)}
                          />
                        </Field>
                      </Box>
                    </>
                  )}

                  {drawerTab === 3 && (
                    <>
                      <Field label="Brands with good buy price" wide>
                        <TextField
                          fullWidth
                          size="small"
                          value={editForm.brands}
                          onChange={(e) =>
                            setEditForm((p) => ({
                              ...p,
                              brands: e.target.value,
                            }))
                          }
                          sx={inputSx(s)}
                        />
                      </Field>
                      <KvBox
                        title="Brands at this supplier"
                        s={s}
                        rows={(Array.isArray(selected?.brands)
                          ? selected.brands
                          : String(editForm.brands || "")
                              .split(",")
                              .map((b) => b.trim())
                              .filter(Boolean)
                        ).map((b) => [b, editForm.price_level || "—"])}
                      />
                    </>
                  )}

                  {drawerTab === 4 && (
                    <>
                      <KvBox
                        title="History"
                        s={s}
                        rows={
                          (selected?.history || []).length
                            ? selected.history.map((h) => [
                                h.created_at
                                  ? new Date(h.created_at).toLocaleString()
                                  : "—",
                                h.note || h.event_type,
                              ])
                            : [["—", "No history yet"]]
                        }
                      />
                      <Box sx={{ mt: 2 }}>
                        <Field label="New entry" wide>
                          <TextField
                            fullWidth
                            size="small"
                            multiline
                            minRows={2}
                            placeholder="What happened?"
                            value={historyNote}
                            onChange={(e) => setHistoryNote(e.target.value)}
                            sx={inputSx(s)}
                          />
                        </Field>
                        <Button
                          sx={{ ...btnSx, mt: 1.25 }}
                          onClick={handleAddHistory}
                        >
                          Add entry
                        </Button>
                      </Box>
                    </>
                  )}
                </Box>

                <Stack
                  direction="row"
                  justifyContent="flex-end"
                  spacing={1}
                  sx={{
                    px: 2.75,
                    py: 1.75,
                    borderTop: `1px solid ${s.theadBorder}`,
                    background: s.footBg,
                  }}
                >
                  <Button onClick={() => setDrawerOpen(false)} sx={btnSx}>
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    disabled={saving}
                    onClick={handleSave}
                    sx={{
                      ...btnSx,
                      background: s.blue,
                      color: "#fff",
                      fontWeight: 600,
                      "&:hover": { background: "#3b7bf5" },
                    }}
                  >
                    Save
                  </Button>
                </Stack>
              </Box>
            </Drawer>
          </>
        )}
      </MDBox>
    </DashboardLayout>
  );
};

export default WholesalersPage;
